import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../services/api";
import type { PaymentRequest } from "../types/payment";
import { PAYMENT_BASE_URL, paymentKeys } from "./queryKeys";

export interface BulkActionPayload {
  request_ids: number[];
  action: "APPROVE" | "REJECT";
  reason?: string;
}

export interface BulkActionResult {
  succeeded: number[];
  failed: { request_id: number; status: number; reason: string }[];
}

function paymentAction(id: number, body: { action: string; reason?: string }): Promise<PaymentRequest> {
  return api.post(`${PAYMENT_BASE_URL}/${id}/action`, body);
}

function bulkPaymentAction(body: BulkActionPayload): Promise<BulkActionResult> {
  return api.post(`${PAYMENT_BASE_URL}/bulk/action`, body);
}

export function useApprovePayment() {
  return useMutation((id: number) => paymentAction(id, { action: "APPROVE" }));
}

export function useRejectPayment() {
  return useMutation(({ id, reason }: { id: number; reason: string }) =>
    paymentAction(id, { action: "REJECT", reason })
  );
}

export function useBulkActionPayment() {
  return useMutation((payload: BulkActionPayload) => bulkPaymentAction(payload));
}

// ---------------------------------------------------------------------------
// Create payment request (manual trigger from InvoiceDetail for APPROVED invoices)
// ---------------------------------------------------------------------------

export interface CreatePaymentRequestResult {
  invoice_id: number;
  created: boolean;
  /** CREATED | ADVANCE_COVERED | … — distinguishes the two `created: false` outcomes. */
  reason?: string;
  /** Server-composed, user-facing sentence; prefer this over a hardcoded message. */
  message?: string;
}

function createPaymentRequest(invoiceId: number): Promise<CreatePaymentRequestResult> {
  return api.post(`${PAYMENT_BASE_URL}/requests`, { invoice_id: invoiceId });
}

export function useCreatePaymentRequest() {
  const queryClient = useQueryClient();
  return useMutation((invoiceId: number) => createPaymentRequest(invoiceId), {
    onSuccess: () => {
      queryClient.invalidateQueries(paymentKeys.all);
    },
  });
}

// ---------------------------------------------------------------------------
// Bulk UTR upload
// ---------------------------------------------------------------------------

export interface BulkUploadResult {
  processed_requests: number;
  results: {
    request_id: number;
    request_status: string;
    recorded_count: number;
    captured_amount: number;
    remaining_amount: number;
  }[];
  errors: string[];
}

function uploadBulkPayments(formData: FormData): Promise<BulkUploadResult> {
  return api.post(`${PAYMENT_BASE_URL}/bulk/payments`, formData);
}

export function useUploadBulkPayments() {
  return useMutation((formData: FormData) => uploadBulkPayments(formData));
}

// ---------------------------------------------------------------------------
// Create batch
// ---------------------------------------------------------------------------

export interface CreateBatchResult {
  batch_ids: string[];
  sent: string[];
  failed: string[];
}

export function useCreateBatch() {
  return useMutation((vendor_facility_id: number) =>
    api.post(`${PAYMENT_BASE_URL}/batches`, { vendor_facility_id }) as Promise<CreateBatchResult>
  );
}
